import { z } from "zod";
import { IsoDateTimeSchema } from "./common.js";

/**
 * Operational settings. Each Backend Block owns the settings it ENFORCES —
 * there is no global config service, because a shared settings store would
 * couple every domain to one schema and one database.
 *
 * Hard safety bounds stay in code (design §14): a setting can tighten
 * behaviour within its declared range, never disable a guard.
 */
export const SettingValueSchema = z.union([z.number(), z.boolean(), z.string()]);
export type SettingValue = z.infer<typeof SettingValueSchema>;

export const SettingSchema = z.object({
	key: z.string().min(1),
	displayName: z.string().min(1),
	description: z.string(),
	valueType: z.enum(["NUMBER", "BOOLEAN", "STRING"]),
	value: SettingValueSchema,
	defaultValue: SettingValueSchema,
	/** Inclusive bounds for NUMBER settings. Enforced server-side. */
	min: z.number().nullable(),
	max: z.number().nullable(),
	/** Which block enforces this setting. */
	ownedBy: z.string(),
	updatedBy: z.string().nullable(),
	updatedAt: IsoDateTimeSchema.nullable(),
});
export type Setting = z.infer<typeof SettingSchema>;

export const SettingsResponseSchema = z.object({
	service: z.string(),
	settings: z.array(SettingSchema),
});
export type SettingsResponse = z.infer<typeof SettingsResponseSchema>;

export const UpdateSettingRequestSchema = z.object({
	value: SettingValueSchema,
	reason: z.string().min(1).max(500),
});
export type UpdateSettingRequest = z.infer<typeof UpdateSettingRequestSchema>;
